import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import AppTextInput, {
    AppTextInputCallables,
} from '../components/common/input/AppTextInput';
import AppButton from '../components/common/input/AppButton';
import {
    useGetPublicProfileQuery,
    useGetPublicTeamQuery,
} from '../app/services/api';
import { useAppSelector } from '../app/hooks';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { setPublicProfile, setPublicTeam } from '../store/profileSlice';
import LoadingScreen from '../components/common/layout/LoadingScreen';
import { formatCurrency } from '../utils/common/numberUtils';
import { CheckoutDetails } from '../models/checkoutDetails';
import { setCheckoutDetails } from '../store/checkoutSlice';
import TipButtons from '../components/tip/TipButtons';
import TipServiceCharge from '../components/tip/TipServiceCharge';
import MainContainer from '../components/common/layout/MainContainer';
import { BrandStyling } from '../theme/brandStyling';

const Tip: React.FC = () => {
    const params = useParams();
    const dispatch = useDispatch();

    const [noUserFound, setNoUserFound] = useState<boolean>(false);
    const [tipValue, _setTipValue] = useState<number>(0);
    const [serviceCharge, setServiceCharge] = useState<number | undefined>(0);
    const [textInputCallables, setTextInputCallables] =
        useState<AppTextInputCallables>();

    const { tipRules, publicProfile, publicTeam } = useAppSelector(
        (state) => state.profile,
    );

    const getPublicProfileResponse = useGetPublicProfileQuery(
        params.memberId!,
        {
            refetchOnMountOrArgChange: true,
            skip: false,
        },
    );
    const getPublicTeamQueryResponse = useGetPublicTeamQuery(params.memberId!, {
        refetchOnMountOrArgChange: true,
        skip: false,
    });
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [tipIsValid, setTipIsValid] = useState<boolean>(false);
    const [tipAmountErrorText, setTipAmountErrorText] = useState<string>('');

    useEffect(() => {
        setIsLoading(
            getPublicProfileResponse.isLoading ||
                getPublicTeamQueryResponse.isLoading,
        );
    }, [
        getPublicProfileResponse.isLoading,
        getPublicTeamQueryResponse.isLoading,
    ]);

    useEffect(() => {
        if (!isLoading) {
            if (getPublicProfileResponse.isSuccess) {
                dispatch(
                    setPublicProfile(getPublicProfileResponse.data.content),
                );
            } else if (getPublicTeamQueryResponse.isSuccess) {
                dispatch(
                    setPublicTeam(getPublicTeamQueryResponse.data.content),
                );
            } else if (
                getPublicTeamQueryResponse.isError &&
                getPublicTeamQueryResponse.isError
            ) {
                setNoUserFound(true);
            }
        }
    }, [
        isLoading,
        getPublicProfileResponse,
        getPublicTeamQueryResponse,
        dispatch,
    ]);

    const navigate = useNavigate();

    const profileName = publicProfile?.firstName ?? publicTeam?.name;
    const uniqueId = publicProfile?.id ?? publicTeam?.id;
    const accountType = publicProfile === undefined ? 'Team' : 'Member';
    const brandId = publicProfile?.brandId ?? publicTeam?.brandId ?? 1;
    const countryCode = publicProfile
        ? publicProfile.countryCode
        : publicTeam?.countryCode;
    const currency = countryCode === 'GB' ? 'GBP' : 'EUR';
    const currencySymbol = countryCode === 'GB' ? '£' : '€';

    const roundDown = (number: number) => {
        return Math.floor(number * 100) / 100;
    };

    const setTipValue = (value: number): void => {
        _setTipValue(value);
        validateTip(value);
        if (value > (tipRules?.enhancedTipThresholdValue ?? 1.0)) {
            const onePercentOfTip = value / 100;
            const standardCharge = tipRules?.standardChargeValue ?? 0.25;
            const extraChargePremium = tipRules?.enhancedTipPercentage ?? 5;
            const extraCharge = onePercentOfTip * extraChargePremium;
            const serviceCharge = roundDown(standardCharge + extraCharge);
            setServiceCharge(serviceCharge);
        } else {
            setServiceCharge(tipRules?.standardChargeValue);
        }
    };

    const validateTip = async (tipValue: number) => {
        if (!tipValue || tipValue < tipRules?.minTipValue!) {
            setTipIsValid(false);
            setTipAmountErrorText(
                `The minimum tip you can give is ${formatCurrency(
                    tipRules?.minTipValue ?? 0,
                    currency,
                )}`,
            );
        } else if (tipValue > tipRules?.maxTipValue!) {
            setTipIsValid(false);
            setTipAmountErrorText(
                `The maximum tip you can give is ${formatCurrency(
                    tipRules?.maxTipValue ?? 0,
                    currency,
                )}`,
            );
        } else {
            setTipIsValid(true);
            setTipAmountErrorText('');
        }
    };

    const presetOnClick = (presetValue: number): void => {
        setTipValue(presetValue);
        if (textInputCallables) {
            textInputCallables.setInput(presetValue);
        }
    };

    const setCheckoutDetailsState = () => {
        let orderCodeDetails: CheckoutDetails = {
            tipValue: tipValue,
            serviceCharge: serviceCharge,
            accountType: accountType,
            uniqueId: uniqueId,
        };
        dispatch(setCheckoutDetails(orderCodeDetails));
    };

    if (noUserFound) {
        return <Navigate to={'/'} replace={true} />;
    }

    if (isLoading) {
        return (
            <MainContainer className="bg-background" brandId={brandId}>
                <div className="h-full flex flex-1 flex-col items-center justify-center w-full max-w-full px-14 md:px-6">
                    <LoadingScreen />
                </div>
            </MainContainer>
        );
    }

    return (
        <MainContainer className="bg-background" isBackIconShown={true} isHeaderShown={true} brandId={brandId}>
            <div className="flex-1 flex flex-col items-center">
                <div className="h-full flex flex-col items-center w-full px-5 py-2 md:px-0 lg:py-0">
                    {BrandStyling.byBrand(brandId).sendTipIcon}
                    <h6 className="mx-3 mt-4 text-center">
                        You can tip {profileName}
                    </h6>
                    <p className="text-primary-2 mt-1 text-[22px] font-medium leading-tight">
                        #{uniqueId}
                    </p>
                    <TipButtons
                        selectedTipValue={tipValue}
                        onClickHandler={presetOnClick}
                        tipButtonValues={tipRules?.tipButtonValues ?? []}
                        brandId={brandId}
                        currencySymbol={currencySymbol}
                    />
                    <div className="flex items-center w-full my-1 text-primary-2 text-sm leading-normal mt-8">
                        <hr className="flex-grow" />
                        <span className="mx-3.5 font-light pb-1">
                            Or enter manually
                        </span>
                        <hr className="flex-grow" />
                    </div>
                    <AppTextInput
                        label="Tip amount"
                        placeholder=""
                        mode="currency"
                        id={'tip-amount-input'}
                        currencySymbol={currencySymbol}
                        setValue={(value: string) =>
                            setTipValue(parseFloat(value))
                        }
                        className={`w-full mt-4`}
                        brand={brandId}
                        setCallables={setTextInputCallables}
                        errorText={tipAmountErrorText}
                    />
                    <TipServiceCharge
                        serviceCharge={serviceCharge}
                        currency={currency}
                        tipRules={tipRules}
                        brandId={brandId}
                    />
                </div>
            </div>
            <div className="w-full pb-10 md:pb-0 px-5 py-5 pt-5 md:pt-8 md:px-0">
                <AppButton
                    className="mt-2"
                    sizeClassName="w-full h-12"
                    value="Continue"
                    isEnabled={tipValue !== undefined && tipIsValid}
                    branding={brandId}
                    onClick={() => {
                        setCheckoutDetailsState();
                        navigate('/payment/customerDetails');
                    }}
                />
            </div>
        </MainContainer>
    );
};

export default Tip;
